import { motion } from "framer-motion";
import { MapPin, Target, Sparkles, Plus, ArrowLeft, Edit, Trash2, RefreshCw, ArrowUp, ArrowDown } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

interface TimelineEvent {
  id: string;
  title: string;
  target_date: string;
  location?: string;
}

interface PivotalStep {
  id: string;
  title: string;
  is_completed: boolean;
  target_date?: string;
}

interface VisualTimelineMapProps {
  events: TimelineEvent[];
  selectedEvent: TimelineEvent | null;
  pivotalSteps: PivotalStep[];
  onSelectEvent: (event: TimelineEvent | null) => void;
  onAddEvent: () => void;
  onEditEvent: (event: TimelineEvent) => void;
  onDeleteEvent: (eventId: string) => void;
  onAddStep: () => void;
  onEditStep: (step: PivotalStep) => void;
  onDeleteStep: (stepId: string) => void;
  onReorderStep: (stepId: string, direction: 'up' | 'down') => void;
  onRefresh: () => void;
}

export default function VisualTimelineMap({
  events,
  selectedEvent,
  pivotalSteps,
  onSelectEvent,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onAddStep,
  onEditStep,
  onDeleteStep,
  onReorderStep,
  onRefresh
}: VisualTimelineMapProps) {
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.target_date).getTime() - new Date(b.target_date).getTime()
  );

  const completedSteps = pivotalSteps.filter(s => s.is_completed).length;
  const totalSteps = pivotalSteps.length;
  const progressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  const selectedEventSteps = selectedEvent 
    ? pivotalSteps.filter(step => step.target_date && selectedEvent.target_date)
    : [];

  return (
    <div className="relative w-full min-h-[600px] p-8 rounded-xl bg-gradient-to-br from-background via-muted/20 to-background overflow-hidden">
      {/* Control Bar */}
      <div className="relative z-20 mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {selectedEvent && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectEvent(null)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to All Events
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {selectedEvent && (
            <Button
              size="sm"
              onClick={onAddStep}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Step
            </Button>
          )}
          <Button
            size="sm"
            onClick={onAddEvent}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Destination
          </Button>
        </div>
      </div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-primary/20 rounded-full"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.2, 0.5, 0.2],
              scale: [1, 1.5, 1],
              x: `${Math.random() * 100}%`,
              y: `${Math.random() * 100}%`,
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Route Progress Summary - Top */}
      {selectedEvent && selectedEventSteps.length > 0 && (
        <motion.div
          className="relative z-10 mb-8 p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-primary/30"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-foreground">Route to: {selectedEvent.title}</h3>
            <span className="text-sm text-muted-foreground">{completedSteps} of {totalSteps} steps completed</span>
          </div>
          <div className="relative h-3 bg-muted/50 rounded-full overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-accent rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
            <motion.div
              className="absolute inset-y-0 left-0 bg-primary/30 rounded-full"
              animate={{ 
                x: ['0%', `${progressPercent}%`],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ width: '20%' }}
            />
          </div>
        </motion.div>
      )}

      {/* Current Position - Starting Point */}
      <motion.div
        className="relative z-10 flex flex-col items-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="relative"
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center shadow-lg">
            <Sparkles className="w-8 h-8 text-primary-foreground" />
          </div>
          <motion.div
            className="absolute inset-0 rounded-full bg-primary/30"
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
        <motion.p
          className="mt-3 text-lg font-semibold text-primary"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          You Are Here
        </motion.p>
        <p className="text-sm text-muted-foreground">{format(new Date(), "MMMM d, yyyy")}</p>
      </motion.div>

      {/* Journey Path - Show Steps if Event Selected, Otherwise Show Destinations */}
      {selectedEvent && selectedEventSteps.length > 0 ? (
        /* Individual Step Boxes */
        <div className="relative z-10 flex flex-col items-center space-y-12">
          {selectedEventSteps.map((step, index) => (
            <motion.div
              key={step.id}
              className="relative w-full max-w-2xl"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              {/* Connecting Line from previous step */}
              {index === 0 && (
                <motion.div
                  className="absolute bottom-full left-1/2 w-0.5 h-12 -translate-x-1/2 bg-gradient-to-b from-primary via-primary/50 to-transparent"
                  initial={{ height: 0 }}
                  animate={{ height: 48 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                />
              )}

              {/* Step Box */}
              <motion.div
                className={`
                  relative p-5 rounded-2xl backdrop-blur-sm transition-all duration-300
                  ${step.is_completed
                    ? 'bg-primary/20 border-2 border-primary shadow-[0_0_20px_rgba(203,178,129,0.3)]'
                    : 'bg-card/50 border border-border'
                  }
                `}
                whileHover={{ scale: 1.02 }}
              >
                {/* Step Number Icon */}
                <div className="absolute -left-4 top-1/2 -translate-y-1/2">
                  <motion.div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center shadow-lg font-bold
                      ${step.is_completed
                        ? 'bg-gradient-to-br from-primary to-accent text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                      }
                    `}
                    animate={step.is_completed ? {
                      scale: [1, 1.1, 1],
                    } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {step.is_completed ? '✓' : index + 1}
                  </motion.div>
                </div>

                <div className="ml-8 flex-1">
                  <h4 className="text-lg font-semibold mb-1 text-foreground">{step.title}</h4>
                  {step.target_date && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        📅
                      </motion.div>
                      {format(new Date(step.target_date), "MMM d, yyyy")}
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 ml-4">
                  {index > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onReorderStep(step.id, 'up')}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                  )}
                  {index < selectedEventSteps.length - 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onReorderStep(step.id, 'down')}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEditStep(step)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => onDeleteStep(step.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Completion glow effect */}
                {step.is_completed && (
                  <motion.div
                    className="absolute inset-0 rounded-2xl bg-primary/10 pointer-events-none"
                    animate={{ 
                      opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}
              </motion.div>

              {/* Connecting line to next step */}
              {index < selectedEventSteps.length - 1 && (
                <motion.div
                  className="absolute top-full left-1/2 w-0.5 h-12 -translate-x-1/2 bg-gradient-to-b from-primary/50 via-primary/30 to-transparent"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 48, opacity: 1 }}
                  transition={{ duration: 0.6, delay: (index + 1) * 0.15 }}
                >
                  {/* Animated traveling dot */}
                  <motion.div
                    className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-primary rounded-full"
                    animate={{ y: [0, 48] }}
                    transition={{ 
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                </motion.div>
              )}
            </motion.div>
          ))}

          {/* Final Destination Marker */}
          <motion.div
            className="relative w-full max-w-2xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: selectedEventSteps.length * 0.15 }}
          >
            <motion.div
              className="absolute bottom-full left-1/2 w-0.5 h-12 -translate-x-1/2 bg-gradient-to-b from-primary via-primary/50 to-transparent"
              initial={{ height: 0 }}
              animate={{ height: 48 }}
              transition={{ duration: 0.6 }}
            />
            <motion.div
              className="relative p-6 rounded-2xl bg-primary/30 border-2 border-primary backdrop-blur-sm"
              animate={{ 
                boxShadow: [
                  '0 0 20px rgba(203,178,129,0.3)',
                  '0 0 40px rgba(203,178,129,0.5)',
                  '0 0 20px rgba(203,178,129,0.3)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="absolute -left-4 top-1/2 -translate-y-1/2">
                <motion.div
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Target className="w-6 h-6 text-primary-foreground" />
                </motion.div>
              </div>
              <div className="ml-12">
                <h3 className="text-xl font-bold text-foreground mb-1">{selectedEvent.title}</h3>
                <p className="text-sm text-muted-foreground">🎯 Destination Reached</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      ) : sortedEvents.length > 0 ? (
        /* Destination Selection View */
        <div className="relative z-10 flex flex-col items-center space-y-16">
          {sortedEvents.map((event, index) => {
            const isSelected = selectedEvent?.id === event.id;

            return (
              <motion.div
                key={event.id}
                className="relative w-full max-w-2xl"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Connecting Line */}
                {index === 0 && (
                  <motion.div
                    className="absolute bottom-full left-1/2 w-0.5 h-16 -translate-x-1/2 bg-gradient-to-b from-primary via-primary/50 to-transparent"
                    initial={{ height: 0 }}
                    animate={{ height: 64 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  />
                )}

                {/* Event Node */}
                <motion.button
                  onClick={() => onSelectEvent(event)}
                  className="relative w-full"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className={`
                      relative p-6 rounded-2xl backdrop-blur-sm transition-all duration-300
                      ${isSelected 
                        ? 'bg-primary/20 border-2 border-primary shadow-[0_0_30px_rgba(203,178,129,0.3)]' 
                        : 'bg-card/50 border border-border hover:border-primary/50'
                      }
                    `}
                  >
                    {/* Destination Icon */}
                    <div className="absolute -left-4 top-1/2 -translate-y-1/2">
                      <motion.div
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg"
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Target className="w-6 h-6 text-primary-foreground" />
                      </motion.div>
                    </div>

                    <div className="ml-12 flex-1">
                      <h3 className="text-xl font-bold mb-2 text-foreground">{event.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            📅
                          </motion.div>
                          {format(new Date(event.target_date), "MMMM d, yyyy")}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.location.substring(0, 30)}...
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </motion.button>

                {/* Action buttons */}
                <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-background/50 backdrop-blur-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEvent(event);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-background/50 backdrop-blur-sm text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteEvent(event.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Connecting line to next event */}
                {index < sortedEvents.length - 1 && (
                  <motion.div
                    className="absolute top-full left-1/2 w-0.5 h-16 -translate-x-1/2 bg-gradient-to-b from-primary/50 via-primary/30 to-transparent"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 64, opacity: 1 }}
                    transition={{ duration: 0.8, delay: (index + 1) * 0.2 }}
                  >
                    {/* Animated traveling dot */}
                    <motion.div
                      className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-primary rounded-full"
                      animate={{ y: [0, 64] }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      ) : null}

      {/* Empty state */}
      {sortedEvents.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center py-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <Target className="w-20 h-20 text-muted-foreground/30 mb-4" />
          </motion.div>
          <p className="text-lg text-muted-foreground">
            Create your first destination to see your journey map
          </p>
        </motion.div>
      )}
    </div>
  );
}
